(() => {
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL'];
  const SIZE_PATTERN = /^(?:XXS|XS|S|M|L|XL|XXL|[2-4]XL|US\s*\d+|\d{1,2})$/i;

  function parseProduct(card) {
    try {
      return JSON.parse(card.dataset.jsonProduct || '{}');
    } catch (error) {
      return null;
    }
  }

  function imageSource(image) {
    if (!image) return '';
    if (image.currentSrc) return image.currentSrc;
    if (image.src) return image.src;
    const srcset = image.dataset.srcset || image.getAttribute('srcset') || '';
    return srcset.split(',')[0]?.trim().split(' ')[0] || image.dataset.src || '';
  }

  function sizeForVariant(variant) {
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    return options.find((option) => SIZE_PATTERN.test(String(option).trim())) || options.at(-1) || '';
  }

  function sizeVariants(product) {
    const grouped = new Map();
    (product.variants || []).forEach((variant) => {
      const label = String(sizeForVariant(variant)).trim();
      if (!label) return;
      const key = label.toUpperCase();
      if (!grouped.has(key)) grouped.set(key, { label, variants: [] });
      grouped.get(key).variants.push(variant);
    });

    return [...grouped.values()]
      .map((group) => ({
        label: group.label,
        variant: group.variants.find((variant) => variant.available) || group.variants[0],
        available: group.variants.some((variant) => variant.available)
      }))
      .sort((a, b) => {
        const aIndex = SIZE_ORDER.indexOf(a.label.toUpperCase());
        const bIndex = SIZE_ORDER.indexOf(b.label.toUpperCase());
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
  }

  function updateCartDrawer() {
    if (window.Shopify?.getCart && window.halo?.updateSidebarCart) {
      window.Shopify.getCart((cart) => {
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
        });
        document.body.classList.add('cart-sidebar-show');
        window.halo.updateSidebarCart(cart);
      });
      return;
    }
    document.querySelector('[data-cart-sidebar]')?.click();
  }

  async function addVariant(button, status) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    status.textContent = 'Adding…';
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unavailable');
      status.textContent = 'Added';
      updateCartDrawer();
      window.setTimeout(() => { status.textContent = ''; }, 1600);
    } catch (error) {
      status.textContent = 'Unavailable';
    } finally {
      button.classList.remove('is-loading');
    }
  }

  function moveWishlist(card) {
    const wishlist = card.querySelector('.card-wishlist');
    const information = card.querySelector('.card-information__wrapper');
    if (!wishlist || !information || wishlist.classList.contains('ivno-wishlist')) return;
    wishlist.classList.add('ivno-wishlist');
    information.appendChild(wishlist);
  }

  function createControls(card, product) {
    const wrapper = card.querySelector('.card-product__wrapper');
    const media = card.querySelector('.card-media');
    const images = media ? [...media.querySelectorAll(':scope > img')].slice(0, 2) : [];
    if (!wrapper || !media || !images.length) return;

    const controls = document.createElement('div');
    controls.className = 'ivno-card-controls';

    const thumbnails = document.createElement('div');
    thumbnails.className = 'ivno-card-thumbs';
    images.forEach((image, index) => {
      const source = imageSource(image);
      if (!source) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ivno-card-thumb${index === 0 ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `View product image ${index + 1}`);
      button.innerHTML = `<img src="${source}" alt="" loading="lazy">`;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        media.classList.toggle('ivno-image-2', index === 1);
        thumbnails.querySelectorAll('.ivno-card-thumb').forEach((thumb, thumbIndex) => {
          thumb.classList.toggle('is-active', thumbIndex === index);
        });
      });
      thumbnails.appendChild(button);
    });

    const sizes = document.createElement('div');
    sizes.className = 'ivno-card-sizes';
    const status = document.createElement('span');
    status.className = 'ivno-card-status';
    status.setAttribute('aria-live', 'polite');

    sizeVariants(product).forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ivno-card-size';
      button.textContent = item.label;
      button.dataset.variantId = item.variant?.id || '';
      button.disabled = !item.available;
      button.setAttribute(
        'aria-label',
        item.available ? `Add size ${item.label} to bag` : `Size ${item.label} unavailable`
      );
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        addVariant(button, status);
      });
      sizes.appendChild(button);
    });

    if (thumbnails.children.length) controls.appendChild(thumbnails);
    controls.appendChild(sizes);
    controls.appendChild(status);
    wrapper.appendChild(controls);
  }

  function decorate(root = document) {
    root.querySelectorAll('.product-item:not([data-ivno-enhanced])').forEach((card) => {
      const product = parseProduct(card);
      if (!product) return;
      card.dataset.ivnoEnhanced = 'true';
      createControls(card, product);
      moveWishlist(card);
    });
  }

  function createCollectionBar() {
    if (document.querySelector('.ivno-collection-bar')) return;
    const heading = document.querySelector('.articleLookbook-title');
    const headingWrap = heading?.closest('.articleLookbook-item');
    const toolbar = document.querySelector('toolbar-item.toolbar');
    const sort = toolbar?.querySelector('.toolbar-sort');
    if (!heading || !headingWrap || !toolbar || !sort) return;

    const bar = document.createElement('div');
    bar.className = 'ivno-collection-bar';
    const title = document.createElement('span');
    title.className = 'ivno-collection-bar__title';
    title.textContent = heading.textContent.trim();
    bar.appendChild(title);
    bar.appendChild(sort);
    headingWrap.classList.add('ivno-original-heading');
    toolbar.classList.add('ivno-toolbar-shell');
    toolbar.parentNode.insertBefore(bar, toolbar);
  }

  function start() {
    createCollectionBar();
    decorate();
    const grid = document.querySelector('#CollectionProductGrid') || document.querySelector('[id*="product-grid"]');
    if (grid) {
      new MutationObserver(() => decorate(grid)).observe(grid, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', (event) => decorate(event.target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
